import constant from "../../constant";
const resource = constant.api + "items";

const state = {
  items: [],
  item: {},
};

// getters
const getters = {
  getAll: (state) => {
    return state.items;
  },
  getItem: (state) => {
    return state.item;
  },
};

// actions
const actions = {
  async getAll({ commit }) {
    const data = await this._vm.$api.fetch(resource);
    commit("setAll", data);
    return data;
  },

  async show({ commit }, reference) {
    const response = await this._vm.$api.show(`${resource}/${reference}`);
    // Extract data from response - handle both nested and direct responses
    let data = null;
    if (response && typeof response === "object") {
      if (response.data) {
        // If response has a data property, use it
        data = response.data;
      } else if (Array.isArray(response)) {
        // If response is an array, use first element
        data = response[0] || response;
      } else {
        // Otherwise use response directly
        data = response;
      }
    }
    commit("setItem", data);
    return data;
  },

  async store({ dispatch }, data) {
    const response = await this._vm.$api.create(resource, data);
    await dispatch("getAll");
    return response;
  },

  async update({ dispatch }, { reference, data }) {
    console.log("Item update action called:", { reference, data });
    try {
      const response = await this._vm.$api.update(
        `${resource}/${reference}`,
        data
      );
      console.log("Item update response:", response);
      await dispatch("show", reference);
      return response;
    } catch (error) {
      console.error("Error in item update action:", error);
      throw error;
    }
  },

  async destroy({ dispatch }, reference) {
    const response = await this._vm.$api.delete(`${resource}/${reference}`);
    await dispatch("getAll");
    return response;
  },

  async reset({ commit }) {
    commit("resetItem");
  },

  async generateBarcode({ dispatch }, reference) {
    try {
      const response = await this._vm.$api.show(
        `${resource}/${reference}/generate-barcode`
      );
      if (response && response.data) {
        const data = response.data;
        if (data.status === "success" || data.status === "info") {
          // Refresh item data to get updated description
          await dispatch("show", reference);
          if (this._vm.$toast) {
            this._vm.$toast[data.status === "success" ? "success" : "info"](
              data.message
            );
          }
        }
        return data;
      }
    } catch (error) {
      console.error("Error generating barcode:", error);
      if (this._vm.$toast) {
        const message =
          error.response?.data?.message ||
          "Erreur lors de la génération du code-barres";
        this._vm.$toast.error(message);
      }
      throw error;
    }
  },

  async printBarcode({ dispatch }, reference) {
    try {
      const response = await this._vm.$api.instance.get(
        `${resource}/${reference}/print-barcode`,
        {
          responseType: "blob",
          validateStatus: function (status) {
            // Don't throw error for any status, we'll handle it manually
            return status >= 200 && status < 600;
          },
        }
      );

      // Check if response is an error (not a PDF)
      const contentType = response.headers["content-type"] || "";
      
      if (!contentType.includes("application/pdf")) {
        // It's an error response, try to parse as JSON
        let errorMessage = "Erreur lors de l'impression du code-barres";
        try {
          const text = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsText(response.data);
          });
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          console.error("Error parsing error response:", parseError);
        }
        
        if (this._vm.$toast) {
          this._vm.$toast.error(errorMessage);
        }
        throw new Error(errorMessage);
      }

      // It's a valid PDF, create blob and open it
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      
      // Try to open in new window for printing
      const printWindow = window.open(url, "_blank");
      if (printWindow) {
        // Wait for window to load then trigger print
        setTimeout(() => {
          try {
            printWindow.print();
          } catch (e) {
            console.warn("Could not trigger print:", e);
          }
        }, 500);
      } else {
        // Fallback to download if popup blocked
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `barcode-${reference}.pdf`);
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }

      if (this._vm.$toast) {
        this._vm.$toast.success("Code-barres imprimé avec succès");
      }
    } catch (error) {
      console.error("Error printing barcode:", error);
      
      // Handle different error types
      let errorMessage = "Erreur lors de l'impression du code-barres";
      
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsText(error.response.data);
          });
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          console.error("Error parsing blob error:", parseError);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      if (this._vm.$toast) {
        this._vm.$toast.error(errorMessage);
      }
      throw error;
    }
  },

  async downloadBarcodeJpg({ dispatch }, reference) {
    try {
      const response = await this._vm.$api.instance.get(
        `${resource}/download-barcode-jpg/${reference}`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "image/jpeg" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `barcode-${reference}.jpg`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (this._vm.$toast) {
        this._vm.$toast.success("Code-barres téléchargé en JPG avec succès");
      }
    } catch (error) {
      console.error("Error downloading barcode JPG:", error);
      if (this._vm.$toast) {
        const message =
          error.response?.data?.message ||
          "Erreur lors du téléchargement du code-barres";
        this._vm.$toast.error(message);
      }
      throw error;
    }
  },
};

// mutations
const mutations = {
  setAll(state, data) {
    state.items = Array.isArray(data) ? data : data?.data || [];
  },

  setItem(state, data) {
    // Ensure we store the item object directly
    if (!data) {
      state.item = {};
      return;
    }
    // If data has a data property, use it (for nested responses)
    if (
      data.data &&
      typeof data.data === "object" &&
      !Array.isArray(data.data)
    ) {
      state.item = data.data;
    } else if (typeof data === "object" && !Array.isArray(data)) {
      // Use data directly if it's an object
      state.item = data;
    } else {
      state.item = {};
    }
  },

  resetItem(state) {
    state.item = {};
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
